import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, FileText, TrendingUp, Activity, BarChart3, Building, PieChart as PieChartIcon, RefreshCw, LayoutDashboard, Sparkles } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { 
  getAdminSummary, 
  getAdminPerformanceDistribution, 
  getAdminTrends, 
  getCustomers, 
  getAssessmentBreakdown 
} from '../api/dashboard';
import { Card, CardContent } from '../components/ui/Card';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { AnimatedCard, CountUp, GlassCard } from '../components/ui';
import StatCard from '../components/charts/StatCard';
import TrendChart from '../components/charts/TrendChart';
import BarChart from '../components/charts/BarChart';
import PieChart from '../components/charts/PieChart';
import CustomerAdminDashboardEnhanced from './CustomerAdminDashboardEnhanced';
import PredictiveInsights from '../components/analytics/PredictiveInsights';
import ActivityHeatmap from '../components/analytics/ActivityHeatmap';
import toast from 'react-hot-toast';

const AdminDashboardPageEnhanced = () => {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [distribution, setDistribution] = useState(null);
  const [trends, setTrends] = useState([]);
  const [assessmentBreakdown, setAssessmentBreakdown] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [trendDays, setTrendDays] = useState(7);
  const [heatmapData, setHeatmapData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, [trendDays]);

  const loadDashboardData = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      
      const [summaryData, distributionData, trendsData, customersData, breakdownData, heatmapTrends] = await Promise.all([
        getAdminSummary(),
        getAdminPerformanceDistribution(),
        getAdminTrends(trendDays),
        getCustomers(),
        getAssessmentBreakdown(),
        getAdminTrends(30)
      ]);
      
      setSummary(summaryData?.data || summaryData);
      setDistribution(distributionData?.data || distributionData);
      setTrends(trendsData?.data || trendsData || []);
      setCustomers(customersData?.data || customersData || []);
      setAssessmentBreakdown(breakdownData?.data || breakdownData || []);
      setHeatmapData(heatmapTrends?.data || heatmapTrends || []);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      if (error.response?.status !== 401) {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  const handleRefresh = () => {
    loadDashboardData(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (selectedCustomerId) {
    return (
      <div>
        <button
          onClick={() => setSelectedCustomerId(null)}
          className="text-blue-600 hover:text-blue-800 font-semibold mb-4"
        >
          ← Back to Admin Dashboard
        </button>
        <CustomerAdminDashboardEnhanced customerId={selectedCustomerId} inAdminMode />
      </div>
    );
  }

  // Prepare chart data with validation
  const performanceChartData = [
    { name: 'High Performers (80+)', value: distribution?.high || 0 },
    { name: 'Average (50-79)', value: distribution?.average || 0 },
    { name: 'Low (<50)', value: distribution?.low || 0 }
  ];
  
  // Check if distribution has any data (at least 1 non-zero segment)
  const hasDistributionData = performanceChartData.filter(d => d.value > 0).length >= 1;
  const totalDistribution = performanceChartData.reduce((sum, d) => sum + d.value, 0);

  const assessmentTypeData = assessmentBreakdown.map(item => ({
    name: item.test_type === 'bigfive' ? 'BIG FIVE' : item.test_type === 'productivity' ? 'Productivity' : 'Advanced',
    total: parseInt(item.total),
    completed: parseInt(item.completed),
    avgScore: parseFloat(item.avg_score) || 0
  }));
  
  // Check if assessment data exists (any non-zero values)
  const hasAssessmentData = assessmentTypeData.length > 0 && assessmentTypeData.some(d => d.total > 0 || d.completed > 0);
  
  // Check if trends have any data
  const hasTrendData = trends.length > 0 && trends.some(t => parseInt(t.completed) > 0 || parseInt(t.started) > 0);

  return (
    <div>
      {/* Header */}
      <motion.div 
        className="mb-8 flex items-center justify-between"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <LayoutDashboard className="w-6 h-6 mr-2 text-cobalt-600" />
          Admin Dashboard
        </h1>
        <div className="flex items-center space-x-2">
          {lastUpdated && (
            <span className="text-xs text-gray-500">
              Updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center space-x-1 px-3 py-1.5 text-xs bg-cobalt-50 text-cobalt-600 rounded-lg hover:bg-cobalt-100 transition-all disabled:opacity-50 font-medium"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
          </button>
        </div>
      </motion.div>

      {/* Enhanced KPI Cards with Trends */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <AnimatedCard delay={0.1} hover={true}>
          <StatCard
            icon={Users}
            label="Total Users"
            value={<CountUp end={summary?.totalUsers || 0} />}
            trend={summary?.trends?.users}
            color="blue"
          />
        </AnimatedCard>

        <AnimatedCard delay={0.2} hover={true}>
          <StatCard
            icon={Activity}
            label="Total Attempts"
            value={<CountUp end={summary?.totalAttempts || 0} />}
            trend={summary?.trends?.attempts}
            color="green"
          />
        </AnimatedCard>

        <AnimatedCard delay={0.3} hover={true}>
          <StatCard
            icon={TrendingUp}
            label="Completion Rate"
            value={<><CountUp end={summary?.completionRate || 0} suffix="%" /></>}
            trend={summary?.trends?.completion}
            color="purple"
          />
        </AnimatedCard>

        <AnimatedCard delay={0.4} hover={true}>
          <StatCard
            icon={FileText}
            label="Reports Generated"
            value={<CountUp end={summary?.totalReportsGenerated || 0} />}
            trend={summary?.trends?.reports}
            color="orange"
          />
        </AnimatedCard>
        
        <AnimatedCard delay={0.5} hover={true}>
          <StatCard
            icon={Users}
            label="Active Today"
            value={<CountUp end={summary?.activeToday || 0} />}
            trend={summary?.trends?.active}
            color="green"
          />
        </AnimatedCard>
        
        <AnimatedCard delay={0.6} hover={true}>
          <StatCard
            icon={Activity}
            label="Avg Score"
            value={<CountUp end={summary?.avgScore || 0} decimals={1} />}
            trend={summary?.trends?.avgScore}
            color="purple"
          />
        </AnimatedCard>
      </div>

      {/* Charts Row 1: Performance Distribution & Trends & Breakdown */}
      <div className="mb-4">
        <h2 className="text-lg font-bold text-gray-900 mb-1">Performance Analytics</h2>
        <p className="text-sm text-gray-500">Distribution, trends, and assessment breakdown</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Performance Distribution Donut Chart */}
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5 }}>
          <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="p-5">
              <div className="flex items-center space-x-2 mb-4">
                <div className="p-2 rounded-lg bg-gradient-to-br from-purple-50 to-indigo-100">
                  <PieChartIcon className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="text-sm font-bold text-gray-900">
                  Performance Distribution
                </h3>
              </div>
              {hasDistributionData ? (
                <PieChart 
                  data={performanceChartData}
                  colors={['#0066ff', '#00d4ff', '#00b8a9']}
                  height={180}
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-[180px] text-center">
                  <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                    <PieChartIcon className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-sm font-medium text-gray-600 mb-1">No distribution data yet</p>
                  <p className="text-xs text-gray-400">Complete assessments to see performance breakdown</p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Trends Line Chart */}
        <motion.div initial={{ opacity: 0, x: 0 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.6 }}>
          <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <div className="p-2 rounded-lg bg-gradient-to-br from-blue-50 to-cyan-100">
                    <BarChart3 className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="text-sm font-bold text-gray-900">
                    Activity Trends
                  </h3>
                </div>
                <select
                  value={trendDays}
                  onChange={(e) => setTrendDays(parseInt(e.target.value))}
                  className="px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                >
                  <option value={7}>7 Days</option>
                  <option value={14}>14 Days</option>
                  <option value={30}>30 Days</option>
                </select>
              </div>
              {hasTrendData ? (
                <TrendChart
                  data={trends}
                  dataKeys={[
                    { dataKey: 'completed', name: 'Completed' },
                    { dataKey: 'started', name: 'Started' }
                  ]}
                  colors={['#0066ff', '#00d4ff', '#00b8a9', '#8b5cf6']}
                  type="line"
                  height={180}
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-[180px] text-center">
                  <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                    <Activity className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-sm font-medium text-gray-600 mb-1">No activity data</p>
                  <p className="text-xs text-gray-400">Activity will appear as users complete assessments</p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Assessment Breakdown */}
        <motion.div initial={{ opacity: 0, x: 0 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.7 }}>
          <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
            <CardContent className="p-5">
              <div className="flex items-center space-x-2 mb-4">
                <div className="p-2 rounded-lg bg-gradient-to-br from-green-50 to-emerald-100">
                  <BarChart3 className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="text-sm font-bold text-gray-900">
                  Assessment Breakdown
                </h3>
              </div>
              {hasAssessmentData ? (
                <BarChart
                  data={assessmentTypeData}
                  dataKeys={[
                    { dataKey: 'total', name: 'Total' },
                    { dataKey: 'completed', name: 'Completed' }
                  ]}
                  colors={['#0066ff', '#8b5cf6']}
                  height={180}
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-[180px] text-center">
                  <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                    <FileText className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-sm font-medium text-gray-600 mb-1">No assessments yet</p>
                  <p className="text-xs text-gray-400">Assessment data will appear here</p>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Advanced Analytics Section - Lower Visual Priority */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ delay: 0.8 }}
        className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl p-6 -mx-2"
      >
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-700 flex items-center">
            <Sparkles className="w-5 h-5 mr-2 text-purple-600" />
            AI-Powered Analytics
          </h2>
          <p className="text-xs text-gray-500 mt-1">Predictive insights and intelligent recommendations</p>
        </div>
        <div className="space-y-3">
          <PredictiveInsights data={summary} />
          <ActivityHeatmap data={heatmapData} />
        </div>
      </motion.div>
    </div>
  );
};

export default AdminDashboardPageEnhanced;
